import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Upload,
  Trash2,
  Plus,
  Image as ImageIcon,
  ExternalLink
} from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

export const AdminSponsorsPage = () => {
  const navigate = useNavigate();
  const { member } = useAuth();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [newSponsor, setNewSponsor] = useState({ name: '', website_url: '', image_url: '' });
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    if (!member?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchSponsors();
  }, [member, navigate]);

  const fetchSponsors = async () => {
    try {
      const response = await axios.get(`${API_URL}/sponsors`);
      setSponsors(response.data);
    } catch (error) {
      toast.error('Failed to load sponsors');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Max size is 2MB');
      return;
    }

    // Check file type
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.error('Invalid file type. Use PNG, JPEG, GIF, or WebP');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/admin/upload-image?admin_id=${member.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setNewSponsor({ ...newSponsor, image_url: response.data.image_url });
      setPreviewImage(response.data.image_url);
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleAddSponsor = async () => {
    if (!newSponsor.name.trim()) {
      toast.error('Please enter a sponsor name');
      return;
    }
    if (!newSponsor.image_url) {
      toast.error('Please upload a sponsor logo');
      return;
    }

    try {
      await axios.post(`${API_URL}/admin/sponsors?admin_id=${member.id}`, newSponsor);
      toast.success('Sponsor added successfully');
      setNewSponsor({ name: '', website_url: '', image_url: '' });
      setPreviewImage(null);
      fetchSponsors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add sponsor');
    }
  };

  const handleDeleteSponsor = async (sponsor) => {
    if (!window.confirm(`Remove ${sponsor.name} as a sponsor?`)) return;

    try {
      await axios.delete(`${API_URL}/admin/sponsors/${sponsor.id}?admin_id=${member.id}`);
      toast.success('Sponsor removed');
      fetchSponsors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove sponsor');
    }
  };

  if (!member?.is_admin) return null;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Sponsor Banner - Top of Page */}
      <SponsorBanner variant="top-banner" />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-muted-foreground hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <img 
            src={TGA_LOGO} 
            alt="TGA Pro Tour" 
            className="h-10 object-contain cursor-pointer"
            onClick={() => navigate('/dashboard')}
          />
          <div className="w-20" />
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-heading text-white flex items-center gap-3">
            <ImageIcon className="w-8 h-8 text-primary" />
            Manage Sponsors
          </h1>
          <p className="text-muted-foreground mt-1">
            Add sponsor logos to display across all pages
          </p>
        </div>

        {/* Add Sponsor Form */}
        <Card className="bg-card border-white/10 mb-8">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Add New Sponsor</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Sponsor Name *</label>
              <Input
                placeholder="e.g., Acme Corp"
                value={newSponsor.name}
                onChange={(e) => setNewSponsor({ ...newSponsor, name: e.target.value })}
                className="bg-muted border-border"
                data-testid="sponsor-name-input"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Website URL (optional)</label>
              <Input
                placeholder="https://example.com"
                value={newSponsor.website_url}
                onChange={(e) => setNewSponsor({ ...newSponsor, website_url: e.target.value })}
                className="bg-muted border-border"
                data-testid="sponsor-url-input"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Sponsor Logo *</label>
              <div className="flex items-center gap-4">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="sponsor-file-input"
                  />
                  <div className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg border border-border transition-colors">
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Uploading...' : 'Upload Logo'}
                  </div>
                </label>
                {previewImage && (
                  <div className="h-16 w-32 bg-white/10 rounded-lg flex items-center justify-center overflow-hidden">
                    <img src={previewImage} alt="Preview" className="max-h-full max-w-full object-contain" />
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Max 2MB. PNG, JPEG, GIF, or WebP.</p>
            </div>

            <Button
              onClick={handleAddSponsor}
              disabled={!newSponsor.name || !newSponsor.image_url || uploading}
              className="bg-primary hover:bg-primary/90"
              data-testid="add-sponsor-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Sponsor
            </Button>
          </CardContent>
        </Card>

        {/* Sponsors List */}
        <Card className="bg-card border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Current Sponsors ({sponsors.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-pulse text-primary">Loading sponsors...</div>
              </div>
            ) : sponsors.length === 0 ? (
              <div className="text-center py-8">
                <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No sponsors added yet</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {sponsors.map((sponsor) => (
                  <div 
                    key={sponsor.id}
                    className="flex items-center justify-between p-4 bg-muted rounded-lg"
                    data-testid={`sponsor-${sponsor.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-16 w-24 bg-white/10 rounded-lg flex items-center justify-center overflow-hidden">
                        <img 
                          src={sponsor.image_url} 
                          alt={sponsor.name} 
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>
                      <div>
                        <p className="text-white font-medium">{sponsor.name}</p>
                        {sponsor.website_url && (
                          <a 
                            href={sponsor.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary text-sm flex items-center gap-1 hover:underline"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Visit Website
                          </a>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteSponsor(sponsor)}
                      className="border-destructive/50 hover:bg-destructive/20 text-destructive"
                      data-testid={`delete-sponsor-${sponsor.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
